import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { Language, defaultLanguage } from '../locales';
import {
  getUserPreferences,
  subscribeUserPreferences,
  updateUserPreferences,
} from '../utils/userPreferences';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: defaultLanguage,
  setLanguage: () => {},
});

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const prefs = getUserPreferences();
    if (prefs.language && ['ru', 'en'].includes(prefs.language)) {
      return prefs.language;
    }
    const savedLanguage = localStorage.getItem('spellbook_language') as Language;
    return savedLanguage && ['ru', 'en'].includes(savedLanguage) ? savedLanguage : defaultLanguage;
  });

  useEffect(() => {
    return subscribeUserPreferences((prefs) => {
      if (prefs.language && ['ru', 'en'].includes(prefs.language)) {
        setLanguageState((prev) => (prev === prefs.language ? prev : prefs.language!));
      }
    });
  }, []);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    updateUserPreferences({ language: newLanguage });
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
